import { useEffect, useRef, useState } from 'react';
import { cyclesAPI } from '../../../api/cycles';

// HOURLY ONLY — the weekly and daily charts read the STORED window through
// useCycleWindows (DWLF-113 ②a, DWLF-271). Fetch the live state machine's
// per-(symbol, timeframe) inter-pivot gap buffer + computed q25/q50/q75
// quantiles. Powers the chart's "expected window" overlay on the timeframe
// the window store does not cover, so the shaded region matches the band the
// backend uses to gate cycle.{side}.window.{early,mid,late} events there.
//
// Returns:
//   { state: { low, high } | null, loading, error }
//
// `state.low` / `state.high` are null when the corresponding
// CycleStateTable row is missing (fresh symbol, no backfill yet) —
// caller falls back to the chart's own mean-based shading.
//
// One fetch per (symbol, timeframe) change. No polling — the
// quantile band only meaningfully changes when a new confirmed
// pivot lands, which is a slow event on every timeframe; if the user
// reloads the page they get fresh data, otherwise the cached band is
// fine for hours/days.
export const useCycleWindowState = ({ symbol, timeframe, enabled = true }) => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Sequence ref so an out-of-order response from a stale (symbol, tf)
  // can't overwrite a more recent fetch's result. Same pattern as
  // useBackendCycleEvents.
  const requestSeqRef = useRef(0);

  // Reset cached state when (symbol, timeframe) changes so the chart
  // can't briefly render the previous symbol's quantile band on the
  // new symbol while the new fetch is in flight. Mirrors the
  // equivalent reset in useBackendCycleEvents — bugbot caught this
  // on PR #420 review with the helpful note that we're claiming
  // "same pattern" without actually doing the reset. Toggling
  // `enabled` is intentionally NOT a trigger here so opening the Tune
  // panel doesn't throw away cached data unnecessarily.
  useEffect(() => {
    setState(null);
  }, [symbol, timeframe]);

  useEffect(() => {
    if (!enabled || !symbol || !timeframe) {
      setLoading(false);
      setError(null);
      return undefined;
    }
    const seq = ++requestSeqRef.current;
    let cancelled = false;
    setLoading(true);
    setError(null);

    cyclesAPI.getState({ symbol, timeframe })
      .then((data) => {
        if (cancelled || seq !== requestSeqRef.current) {
          return;
        }
        // The handler returns the symbol/timeframe identity alongside
        // the per-side projections; we only care about low/high here.
        setState({
          low: data?.low ?? null,
          high: data?.high ?? null,
        });
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled || seq !== requestSeqRef.current) {
          return;
        }
        // Fail soft: a transient API error shouldn't take down the
        // chart, just disable the new overlay so the chart's own
        // mean-based shading takes over.
        setError(err?.message || 'cycle state fetch failed');
        setState(null);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [symbol, timeframe, enabled]);

  return { state, loading, error };
};

export default useCycleWindowState;
